import { Maximize2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { Button, DataTable } from '@/components/ui'
import type { Transaction } from '@/modules/projects'
import {
  chatTransactionsColumns,
  useAllTransactions,
  useLoadingFirstTransactions,
  useLoadingTransactions,
  useProject,
  useProjectActions,
  useProjectLoaders,
} from '@/modules/projects'
import { captureException } from '@/services/monitoring'
import ExpandedArtifactDialog from './ExpandedArtifactDialog'

export interface TransactionsSpec {
  // Wire field is `transaction_ids`; camelcaseSpec already converted.
  transactionIds: string[]
  title?: string | null
}

interface TransactionsArtifactProps {
  spec: TransactionsSpec
}

// The chat-side transactions artifact. The model emits a list of
// transaction ids (typically from a prior query_transactions call); we
// hydrate the rows from the already-loaded project store and render
// them with the same column set the project Overview uses. No filter
// bar, no full-screen toggle — those belong on the Overview, not in a
// chat artifact. We do keep the expand-to-modal so a long list can
// pop out into a larger surface.
export default function TransactionsArtifact({
  spec,
}: TransactionsArtifactProps) {
  const allTransactions = useAllTransactions()
  const loadingTransactions = useLoadingTransactions()
  const loadingFirstTransactions = useLoadingFirstTransactions()
  const projectSlug = useProject()?.slug
  const { fetchTransactions } = useProjectLoaders()
  const { stopLoadingTransactions } = useProjectActions()
  const requestedTransactionsRef = useRef(false)
  const [expanded, setExpanded] = useState(false)

  // Some project pages never load transactions, so the flag would stay true forever.
  useEffect(() => {
    if (!projectSlug || !loadingFirstTransactions) return
    if (requestedTransactionsRef.current) return
    requestedTransactionsRef.current = true
    fetchTransactions(projectSlug).catch((error) => {
      captureException(error)
      stopLoadingTransactions()
    })
  }, [
    projectSlug,
    loadingFirstTransactions,
    fetchTransactions,
    stopLoadingTransactions,
  ])

  const { rows, missingCount } = useMemo(
    () => resolveRows(allTransactions, spec.transactionIds),
    [allTransactions, spec.transactionIds],
  )

  const hasShareNumber = rows.some((row) => row.shareNumber)
  const columns = useMemo(
    () => chatTransactionsColumns(hasShareNumber),
    [hasShareNumber],
  )

  const isEmpty = rows.length === 0
  const title = spec.title?.trim() || 'Transactions'

  return (
    <>
      <figure className="my-1 w-full overflow-hidden rounded-lg border bg-card">
        <figcaption className="flex items-center justify-between gap-3 border-b px-4 py-2.5 text-sm font-medium text-foreground">
          <span className="min-w-0 flex-1 truncate">
            {title}
            <span className="ml-2 text-xs font-normal text-muted-foreground">
              {countLabel(rows.length, missingCount)}
            </span>
          </span>
          {!isEmpty && (
            <Button
              type="button"
              theme="ghost"
              size="xs"
              isIcon
              onClick={() => setExpanded(true)}
              title="Expand"
              aria-label="Expand"
            >
              <Maximize2 className="size-3.5" aria-hidden />
            </Button>
          )}
        </figcaption>

        {loadingTransactions ? (
          <div className="flex h-80 items-center justify-center px-4 text-sm text-muted-foreground">
            Loading transactions
          </div>
        ) : isEmpty ? (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            None of the named transactions are in this project.
          </div>
        ) : expanded ? (
          // While the dialog owns the table, drop the inline copy
          // entirely — keeping it mounted under the modal doubled the
          // tanstack/virtualizer cost for no visible benefit. Show a
          // stable-height stub so the bubble layout doesn't collapse.
          <InlineStubWhileExpanded />
        ) : (
          <DataTable
            columns={columns}
            data={rows}
            height="h-80"
            enableRowSelection={false}
          />
        )}
      </figure>

      <ExpandedArtifactDialog
        open={expanded}
        onOpenChange={setExpanded}
        title={title}
      >
        <div className="flex flex-1 flex-col overflow-hidden">
          <DataTable
            columns={columns}
            data={rows}
            height="flex-1"
            enableRowSelection={false}
          />
        </div>
      </ExpandedArtifactDialog>
    </>
  )
}

function InlineStubWhileExpanded() {
  return (
    <div className="flex h-80 items-center justify-center px-4 text-sm text-muted-foreground">
      Showing in the expanded view — close to view here.
    </div>
  )
}

// Looks ids up in the loaded store, preserving the order the model
// named. Tracks how many ids didn't resolve so we can footnote the
// header when a project's transactions haven't fully loaded yet (or
// the model hallucinated an id).
function resolveRows(
  transactions: Transaction[],
  ids: string[] | undefined,
): { rows: Transaction[]; missingCount: number } {
  if (!ids || ids.length === 0) {
    return { rows: [], missingCount: 0 }
  }
  const byId = new Map<string, Transaction>()
  for (const transaction of transactions) {
    byId.set(String(transaction.id), transaction)
  }
  const rows: Transaction[] = []
  let missingCount = 0
  for (const id of ids) {
    const match = byId.get(String(id))
    if (match) {
      rows.push(match)
    } else {
      missingCount += 1
    }
  }
  return { rows, missingCount }
}

function countLabel(found: number, missing: number): string {
  if (missing === 0) return `${found.toLocaleString()} ${plural('row', found)}`
  return `${found.toLocaleString()} of ${(found + missing).toLocaleString()} (${missing} not loaded)`
}

function plural(noun: string, count: number): string {
  return count === 1 ? noun : `${noun}s`
}
